import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

function AuthorityEngineer() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authorityData, setAuthorityData] = useState({
    authorityScore: 0,
    ipLibrary: {
      frameworks: 0,
      processes: 0,
      caseStudies: 0
    },
    visibilityHistory: [],
    moduleStatus: {
      authorityAudit: 'not_started',
      mindToMethod: 'not_started',
      processBuilder: 'not_started',
      proofLibrary: 'not_started'
    }
  });

  useEffect(() => {
    fetchAuthorityData();
  }, []);

  const fetchAuthorityData = async () => {
    try {
      const response = await fetch('/api/authority-engineer/dashboard', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAuthorityData({
            authorityScore: data.authorityScore || 0,
            ipLibrary: data.ipLibrary || { frameworks: 0, processes: 0, caseStudies: 0 },
            visibilityHistory: data.visibilityHistory || [],
            moduleStatus: data.moduleStatus || {
              authorityAudit: 'not_started',
              mindToMethod: 'not_started',
              processBuilder: 'not_started',
              proofLibrary: 'not_started'
            }
          });
        }
      }
    } catch (err) {
      console.error('Error fetching authority data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 76) return '#28a745'; // Green - Established
    if (score >= 51) return '#17a2b8'; // Blue - Recognized
    if (score >= 26) return '#ffc107'; // Yellow - Emerging
    return '#6c757d'; // Grey - Building
  };

  const getScoreLabel = (score) => {
    if (score >= 76) return 'Established Thought Leader';
    if (score >= 51) return 'Recognized Expert';
    if (score >= 26) return 'Emerging Authority';
    return 'Building Foundation';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'complete':
        return <Badge bg="success">Complete</Badge>;
      case 'in_progress':
        return <Badge bg="warning" text="dark">In Progress</Badge>;
      default:
        return <Badge bg="secondary">Not Started</Badge>;
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading your authority data...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-4">
      {/* Header */}
      <div className="mb-4">
        <h1>The Authority Engineer</h1>
        <p className="text-muted mb-0">
          Extract, codify, and monetize your intellectual property. Turn your expertise into business assets.
        </p>
      </div>

      {/* Hero Section - Authority Score */}
      <Card className="mb-4 shadow" style={{
        background: `linear-gradient(135deg, ${colors.greyDarkest} 0%, ${colors.greyDark} 100%)`,
        color: 'white'
      }}>
        <Card.Body className="py-4">
          <Row className="align-items-center">
            <Col md={4} className="text-center border-end">
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <div style={{
                  width: '140px',
                  height: '140px',
                  borderRadius: '50%',
                  border: `8px solid ${getScoreColor(authorityData.authorityScore)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  background: 'rgba(255,255,255,0.1)'
                }}>
                  <span style={{ fontSize: '2.5rem', fontWeight: 'bold', color: colors.goldLight }}>
                    {authorityData.authorityScore}
                  </span>
                  <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>/ 100</span>
                </div>
              </div>
              <div className="mt-2">
                <Badge style={{
                  backgroundColor: getScoreColor(authorityData.authorityScore),
                  fontSize: '0.85rem',
                  padding: '0.5em 1em'
                }}>
                  {getScoreLabel(authorityData.authorityScore)}
                </Badge>
              </div>
            </Col>
            <Col md={4} className="text-center border-end py-3">
              <h5 style={{ color: colors.goldLight }}>IP Library</h5>
              <Row className="mt-3">
                <Col>
                  <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>
                    {authorityData.ipLibrary.frameworks}
                  </div>
                  <small className="text-muted">Frameworks</small>
                </Col>
                <Col>
                  <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>
                    {authorityData.ipLibrary.processes}
                  </div>
                  <small className="text-muted">Processes</small>
                </Col>
                <Col>
                  <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>
                    {authorityData.ipLibrary.caseStudies}
                  </div>
                  <small className="text-muted">Case Studies</small>
                </Col>
              </Row>
            </Col>
            <Col md={4} className="text-center py-3">
              <h5 style={{ color: colors.goldLight }}>AI Visibility</h5>
              {authorityData.visibilityHistory.length > 0 ? (
                <div className="mt-2">
                  <small>Last checked: {authorityData.visibilityHistory[0]?.date}</small>
                  <div className="mt-2">
                    <Button
                      variant="outline-light"
                      size="sm"
                      onClick={() => navigate('/command-center/authority-engineer/visibility')}
                    >
                      View History
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <small className="d-block mb-2" style={{ opacity: 0.7 }}>
                    See what AI platforms know about you
                  </small>
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => navigate('/command-center/authority-engineer/audit')}
                  >
                    Run First Check
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Module Cards */}
      <Row>
        {/* Module 1: Authority Audit */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <span className="me-2">1</span>
                  Authority Audit
                </h5>
                {getStatusBadge(authorityData.moduleStatus.authorityAudit)}
              </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
              <Card.Text className="text-muted flex-grow-1">
                Discover what AI platforms know about you, inventory your existing IP,
                and identify gaps and opportunities.
              </Card.Text>
              <div className="mb-3">
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => navigate('/command-center/authority-engineer/visibility')}
                  >
                    Visibility Check
                  </Button>
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => navigate('/command-center/authority-engineer/inventory')}
                  >
                    IP Inventory
                  </Button>
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => navigate('/command-center/authority-engineer/gaps')}
                  >
                    Gap Analysis
                  </Button>
                </div>
              </div>
              <div className="mt-auto">
                <Button
                  variant="dark"
                  className="w-100"
                  style={{ backgroundColor: colors.greyDarkest }}
                  onClick={() => navigate('/command-center/authority-engineer/audit')}
                >
                  {authorityData.moduleStatus.authorityAudit === 'complete' ? 'View Full Audit' : 'Start Guided Audit'}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Module 2: Mind to Method */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <span className="me-2">2</span>
                  Mind to Method
                </h5>
                {getStatusBadge(authorityData.moduleStatus.mindToMethod)}
              </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
              <Card.Text className="text-muted flex-grow-1">
                Extract and codify your intellectual property through guided conversation.
                Turn the expertise in your head into documented, defensible frameworks.
              </Card.Text>
              <div className="mb-3">
                <strong className="d-block mb-2">The 5-Layer Extraction:</strong>
                <ul className="small text-muted mb-0">
                  <li>Your Expert Territory (unique positioning)</li>
                  <li>Pain Patterns (mistakes you prevent)</li>
                  <li>Decision Logic (your framework)</li>
                  <li>Boundaries (what you reject)</li>
                  <li>Language (your terminology)</li>
                </ul>
              </div>
              <div className="mt-auto d-flex flex-column gap-2">
                <Button
                  variant="dark"
                  className="w-100"
                  style={{ backgroundColor: colors.greyDarkest }}
                  onClick={() => navigate('/command-center/authority-engineer/extract')}
                >
                  {authorityData.ipLibrary.frameworks > 0 ? 'Extract More IP' : 'Extract Your First IP'}
                </Button>
                <Button
                  variant="outline-secondary"
                  className="w-100"
                  onClick={() => navigate('/command-center/authority-engineer/import-ip')}
                >
                  Import Existing IP
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Module 3: Process Builder */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <span className="me-2">3</span>
                  Process Builder
                </h5>
                {getStatusBadge(authorityData.moduleStatus.processBuilder)}
              </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
              <Card.Text className="text-muted flex-grow-1">
                Turn your extracted IP into executable systems. Create SOPs, workflows,
                and checklists that others can follow to get consistent results.
              </Card.Text>
              <div className="mb-3">
                <strong className="d-block mb-2">Process Types:</strong>
                <ul className="small text-muted mb-0">
                  <li>Checklists (linear, step-by-step)</li>
                  <li>Decision Frameworks (branching logic)</li>
                  <li>Workflows (multi-person, handoffs)</li>
                  <li>With common mistakes and "done right" criteria</li>
                </ul>
              </div>
              <div className="mt-auto">
                <Button
                  variant="dark"
                  className="w-100"
                  style={{ backgroundColor: colors.greyDarkest }}
                  onClick={() => navigate('/command-center/authority-engineer/processes')}
                >
                  {authorityData.ipLibrary.processes > 0 ? 'Manage Processes' : 'Build Your First Process'}
                </Button>
                {authorityData.ipLibrary.frameworks === 0 && (
                  <small className="text-muted d-block mt-2 text-center">
                    Tip: Extract IP first to link processes to your frameworks
                  </small>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Module 4: Proof Library */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <span className="me-2">4</span>
                  Proof Library
                </h5>
                {getStatusBadge(authorityData.moduleStatus.proofLibrary)}
              </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
              <Card.Text className="text-muted flex-grow-1">
                Document your results. Build credibility with case studies that show
                your IP works in practice. Before/after metrics that prove your value.
              </Card.Text>
              <div className="mb-3">
                <strong className="d-block mb-2">Case Study Elements:</strong>
                <ul className="small text-muted mb-0">
                  <li>Client challenge (what they thought vs. what you saw)</li>
                  <li>IP applied (which frameworks/processes used)</li>
                  <li>Before/after metrics with calculated improvement</li>
                  <li>Lessons learned (feeds back into IP refinement)</li>
                </ul>
              </div>
              <div className="mt-auto">
                <Button
                  variant="dark"
                  className="w-100"
                  style={{ backgroundColor: colors.greyDarkest }}
                  onClick={() => navigate('/command-center/case-study')}
                >
                  {authorityData.ipLibrary.caseStudies > 0 ? 'Manage Case Studies' : 'Document Your First Win'}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Module 5: Content Matrix */}
      <Row>
        <Col lg={12} className="mb-4">
          <Card className="h-100 shadow-sm" style={{ border: `2px solid ${colors.goldMid}` }}>
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <span className="me-2">5</span>
                  Content Matrix
                </h5>
                <Badge bg="info">Content</Badge>
              </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
              <Row>
                <Col md={8}>
                  <p className="text-muted mb-3">
                    Transform your IP into platform-ready content. Generate blog posts, LinkedIn articles,
                    email sequences, video scripts, and more - all derived from your documented frameworks.
                  </p>
                  <div className="mb-3">
                    <strong className="d-block mb-2">Content Types:</strong>
                    <Row>
                      <Col sm={6}>
                        <ul className="small text-muted mb-0">
                          <li>Blog Posts (800-1500 words)</li>
                          <li>LinkedIn Posts (150-300 words)</li>
                          <li>LinkedIn Articles (600-1200 words)</li>
                          <li>Email Sequences (200-400 words)</li>
                        </ul>
                      </Col>
                      <Col sm={6}>
                        <ul className="small text-muted mb-0">
                          <li>Twitter/X Threads (200-400 words)</li>
                          <li>Video Scripts (300-600 words)</li>
                          <li>Podcast Outlines (400-800 words)</li>
                        </ul>
                      </Col>
                    </Row>
                  </div>
                </Col>
                <Col md={4} className="d-flex align-items-center justify-content-end">
                  <Button
                    variant="dark"
                    size="lg"
                    style={{ backgroundColor: colors.greyDarkest }}
                    onClick={() => navigate('/command-center/authority-engineer/content')}
                  >
                    Open Content Matrix
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Module 6: Authority Roadmap */}
      <Row className="mt-2">
        {/* Module 6A: Book Builder */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm" style={{ border: `2px solid ${colors.goldMid}` }}>
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <span className="me-2">6A</span>
                  Book Builder
                </h5>
                <Badge bg="warning" text="dark">Authority</Badge>
              </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
              <p className="text-muted mb-3 flex-grow-1">
                Turn your IP into an authority-building book. Get AI-generated topic options,
                capture your personal "why", and generate a Book Development Kit with outlines and writing prompts.
              </p>
              <div className="d-flex gap-2 flex-wrap mt-auto">
                <Button
                  variant="dark"
                  style={{ backgroundColor: colors.greyDarkest }}
                  onClick={() => navigate('/command-center/authority-engineer/book')}
                >
                  Plan Your Book
                </Button>
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => navigate('/command-center/authority-engineer/amazon-publishing')}
                >
                  Publishing Guide
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Module 6B: Speaking Topics */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm" style={{ border: `2px solid ${colors.goldMid}` }}>
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <span className="me-2">6B</span>
                  Speaking Topics
                </h5>
                <Badge bg="warning" text="dark">Authority</Badge>
              </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
              <p className="text-muted mb-3 flex-grow-1">
                Generate compelling speaking topics from your IP. Create professional speaker one-sheets
                with talk outlines, key takeaways, and booking information.
              </p>
              <div className="mt-auto">
                <Button
                  variant="dark"
                  style={{ backgroundColor: colors.greyDarkest }}
                  onClick={() => navigate('/command-center/authority-engineer/speaking')}
                >
                  Develop Speaking Topics
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Module 6C: Course Creator - Coming Soon */}
        <Col lg={6} className="mb-4">
          <Card className="h-100 shadow-sm" style={{ border: '2px dashed #ccc', opacity: 0.7 }}>
            <Card.Header style={{ backgroundColor: colors.greyDark, color: colors.greyLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <span className="me-2">6C</span>
                  Course Creator
                </h5>
                <Badge bg="secondary">Coming Soon</Badge>
              </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
              <p className="text-muted mb-3 flex-grow-1">
                Transform your book into a structured online course. AI suggests module breakdowns,
                supporting assets for each section, and generates quiz questions and answers.
              </p>
              <div className="mt-auto">
                <Button
                  variant="outline-secondary"
                  disabled
                >
                  Coming Soon
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Coming Soon Section */}
      <Card className="mt-2 border-0" style={{ backgroundColor: '#f8f9fa' }}>
        <Card.Body>
          <h6 className="mb-3" style={{ color: colors.greyDark }}>
            <span className="me-2">🚀</span>
            Coming Soon
          </h6>
          <Row>
            <Col md={12} className="mb-2">
              <div className="d-flex align-items-center">
                <Badge bg="secondary" className="me-2">7</Badge>
                <span className="text-muted">Implementation</span>
                <small className="ms-2 text-muted">(Schema markup, Website integration, Publishing)</small>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Info Section */}
      <Row className="mt-4">
        <Col>
          <Card className="border-0" style={{ backgroundColor: '#fff8e6' }}>
            <Card.Body>
              <h6 className="mb-2" style={{ color: colors.goldDark }}>
                <span className="me-2">💡</span>
                Why Document Your IP?
              </h6>
              <p className="small text-muted mb-0">
                AI can replicate tactics, but not unique frameworks built from your synthesized expertise.
                Documented IP increases your business valuation, enables delegation, creates content opportunities,
                and positions you as a recognized authority that AI platforms cite. Your expertise is an asset -
                this system helps you capture and compound it.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default AuthorityEngineer;
